import { useEffect, useState } from 'react'
import { useLocation } from 'react-router'

import {
  fetchBookingPage,
  keyFromHash,
  type BookingPage,
} from '../../api/transport'

/**
 * The booking behind a private link (M40-T10): the key is read from the URL
 * fragment (`#k=…`) and sent to T08 in a header, never in a URL.
 *
 * Every failure is the same "missing": no key, a wrong or revoked one, an
 * unknown reference, Transport switched off, or the network. The page must
 * not tell them apart (T08 answers one 404 for all of them, too).
 */
export type LinkedBooking =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'ready'; page: BookingPage; manageKey: string }

/** An answer, remembered with what it answered, so a new link never shows
 * the last one's booking. */
type Answer = { reference: string; key: string; page: BookingPage | null }

export function useBookingFromLink(reference: string): {
  state: LinkedBooking
  /** Replace the page after an action (Ready to collect). */
  setPage: (page: BookingPage) => void
} {
  const { hash } = useLocation()
  const manageKey = keyFromHash(hash)
  const [answer, setAnswer] = useState<Answer | null>(null)

  useEffect(() => {
    if (!manageKey) return
    const ctrl = new AbortController()
    fetchBookingPage(reference, manageKey, ctrl.signal).then(
      (page) => setAnswer({ reference, key: manageKey, page }),
      () => {
        if (!ctrl.signal.aborted)
          setAnswer({ reference, key: manageKey, page: null })
      },
    )
    return () => ctrl.abort()
  }, [reference, manageKey])

  let state: LinkedBooking
  if (!manageKey) state = { kind: 'missing' }
  else if (answer?.reference !== reference || answer.key !== manageKey)
    state = { kind: 'loading' }
  else if (answer.page === null) state = { kind: 'missing' }
  else state = { kind: 'ready', page: answer.page, manageKey }

  const setPage = (page: BookingPage) => {
    if (manageKey) setAnswer({ reference, key: manageKey, page })
  }
  return { state, setPage }
}
